'use client'

import { ChevronRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { sanitizeHtml } from '@/_lib/sanitize-html.client'
import { formatPrice, orderFlowPriceFormatOptions } from '@/_lib/utils'
import { IPriceListPromotion } from '@/_types'
import { processLink } from '@/_utils/link.utils'

const DESCRIPTION_PREVIEW_MAX_CHARS = 40

const PREVIEW_EXPAND_BUTTON_ATTR = 'data-home-discount-expand'
const PREVIEW_COLLAPSE_BUTTON_ATTR = 'data-home-discount-collapse'

function decodeHtmlEntities(text: string): string {
	return text
		.replace(/&nbsp;/gi, ' ')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&amp;/g, '&')
}

function htmlToPlainText(html: string): string {
	return decodeHtmlEntities(html.replace(/<\/[^>]+>/g, ' ').replace(/<[^>]*>/g, ''))
		.replace(/\s+/g, ' ')
		.trim()
}

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

function sanitizeDescriptionPreviewWithExpandButton(
	previewHtml: string,
	moreLabel: string,
	shouldSanitize: boolean
): string {
	const button = `<button type="button" class="inline align-baseline hover:underline bg-transparent border-0 p-0 pl-1 cursor-pointer text-inherit font-medium" ${PREVIEW_EXPAND_BUTTON_ATTR}>${escapeHtml(moreLabel)}</button>`
	const html = `${previewHtml} ${button}`
	if (!shouldSanitize) return html
	return sanitizeHtml(html, {
		ADD_TAGS: ['button'],
		ADD_ATTR: [PREVIEW_EXPAND_BUTTON_ATTR]
	})
}

function sanitizeDescriptionWithCollapseButton(
	descriptionHtml: string,
	lessLabel: string,
	shouldSanitize: boolean
): string {
	const button = `<button type="button" class="inline align-baseline hover:underline bg-transparent border-0 p-0 pl-1 cursor-pointer text-inherit font-medium" ${PREVIEW_COLLAPSE_BUTTON_ATTR}>${escapeHtml(lessLabel)}</button>`
	const html = `${descriptionHtml} ${button}`
	if (!shouldSanitize) return html
	return sanitizeHtml(html, {
		ADD_TAGS: ['button'],
		ADD_ATTR: [PREVIEW_COLLAPSE_BUTTON_ATTR]
	})
}

const ADiscountCardPriceRow = ({
	priceListPromotion
}: {
	priceListPromotion: IPriceListPromotion
}) => {
	const router = useRouter()

	const formattedPrice = useMemo(() => {
		return formatPrice(priceListPromotion.price, orderFlowPriceFormatOptions)
	}, [priceListPromotion.price])

	const formattedPriceOld = useMemo(() => {
		return formatPrice(priceListPromotion.priceOld, orderFlowPriceFormatOptions)
	}, [priceListPromotion.priceOld])

	const handleOrderClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		e.preventDefault()

		router.push(`/volne-terminy?type=promotion&id=${priceListPromotion.id}`)
	}

	return (
		<div className='flex flex-row flex-wrap justify-between items-center lg:items-end gap-2 lg:gap-4 w-full min-w-0'>
			<div className='flex justify-start items-end gap-1.5 lg:gap-4 min-w-0'>
				<p className='text-sm lg:text-base whitespace-nowrap'>
					{formattedPrice.price} {formattedPrice.currency}
				</p>
				<p className='text-sm lg:text-base opacity-85 line-through whitespace-nowrap'>
					{formattedPriceOld.price} {formattedPriceOld.currency}
				</p>
			</div>

			<button className='btn_basic white py-0! w-fit shrink-0' onClick={handleOrderClick}>
				<span className='px-1! py-1! md:px-3! text-xs md:text-sm flex-none!'>Objednať</span>
			</button>
		</div>
	)
}

const ADiscountCard = ({ priceListPromotion }: { priceListPromotion: IPriceListPromotion }) => {
	const t = useTranslations('pages.home.home_action')
	const [descriptionExpanded, setDescriptionExpanded] = useState(false)
	const [isMounted, setIsMounted] = useState(false)

	useEffect(() => {
		setIsMounted(true)
	}, [])

	const descriptionHtml = (priceListPromotion.description ?? '').trim()
	const sanitizedDescription = useMemo(
		() => (isMounted ? sanitizeHtml(descriptionHtml) : descriptionHtml),
		[descriptionHtml, isMounted]
	)

	const { descriptionPreviewHtmlForCollapsed, descriptionExpandedHtml } = useMemo(() => {
		const plain = htmlToPlainText(sanitizedDescription)
		const needsExpand = plain.length > DESCRIPTION_PREVIEW_MAX_CHARS
		if (!needsExpand) {
			return {
				descriptionPreviewHtmlForCollapsed: sanitizedDescription,
				descriptionExpandedHtml: sanitizedDescription
			}
		}
		const truncated = `${plain.slice(0, DESCRIPTION_PREVIEW_MAX_CHARS)}...`
		return {
			descriptionPreviewHtmlForCollapsed: sanitizeDescriptionPreviewWithExpandButton(
				escapeHtml(truncated),
				t('showMore'),
				isMounted
			),
			descriptionExpandedHtml: sanitizeDescriptionWithCollapseButton(
				sanitizedDescription,
				t('showLess'),
				isMounted
			)
		}
	}, [isMounted, sanitizedDescription, t])

	const handlePreviewExpandPointer = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (!(e.target as HTMLElement).closest(`[${PREVIEW_EXPAND_BUTTON_ATTR}]`)) return
		e.preventDefault()
		e.stopPropagation()
		setDescriptionExpanded(true)
	}, [])

	const handlePreviewCollapsePointer = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (!(e.target as HTMLElement).closest(`[${PREVIEW_COLLAPSE_BUTTON_ATTR}]`)) return
		e.preventDefault()
		e.stopPropagation()
		setDescriptionExpanded(false)
	}, [])

	const href =
		priceListPromotion.url ?? `/volne-terminy?type=promotion&id=${priceListPromotion.id}`
	const { href: processedHref, target, rel } = processLink(href)

	return (
		<Link
			href={processedHref}
			target={target}
			rel={rel}
			style={{
				backgroundColor: priceListPromotion.webContent.backgroundColor,
				color: priceListPromotion.webContent.textColor
			}}
			prefetch={false}
			className='block min-w-0 w-full rounded-2xl overflow-hidden lg:hover:scale-101 transition-all duration-300 ease-in-out'
		>
			<div className='w-full h-full flex justify-start items-start p-0'>
				<div className='w-[100px] md:w-[135px] h-full'>
					<Image
						src={priceListPromotion.image?.url}
						alt={priceListPromotion.name}
						width={135}
						height={135}
						className='aspect-square object-cover h-full'
						unoptimized={true}
					/>
				</div>

				<div className='p-3 lg:p-4 h-full min-w-0 flex-1 flex justify-between items-center gap-2 lg:gap-4'>
					<div className='h-full min-w-0 flex-1 flex flex-col justify-between gap-2 lg:gap-4'>
						<div className='min-w-0'>
							<h3 className='text-sm lg:text-lg font-medium lg:font-normal w-full wrap-break-word'>
								{priceListPromotion.name}
							</h3>
							<div className='text-xs lg:text-sm wrap-break-word **:m-0'>
								{descriptionExpanded ? (
									<div
										onClick={handlePreviewCollapsePointer}
										onMouseDown={handlePreviewCollapsePointer}
										dangerouslySetInnerHTML={{
											__html: descriptionExpandedHtml
										}}
									/>
								) : (
									<div
										onClick={handlePreviewExpandPointer}
										onMouseDown={handlePreviewExpandPointer}
										dangerouslySetInnerHTML={{
											__html: descriptionPreviewHtmlForCollapsed
										}}
									/>
								)}
							</div>
						</div>

						<ADiscountCardPriceRow priceListPromotion={priceListPromotion} />
					</div>

					<ChevronRight className='w-6 h-6 shrink-0 self-center' aria-hidden />
				</div>
			</div>
		</Link>
	)
}

export default ADiscountCard
